'use client';

import { FC, ReactNode } from 'react';
import { shallow } from 'zustand/shallow';

import { Icon } from '@/general/Icons/Icon';
import { Image } from '@/general/components/Image/Image';
import { Shade } from '@/general/components/Shade/Shade';
import { pdpStore } from '@/stores/pdpStore';
import { DataLayerPdpEvent, type DataLayerPdpPayload, pushToDataLayerPDP } from '@/utils/pushToDataLayerPDP';

type GalleryTriggerType = 'gallery' | 'imperfections' | 'interior';

type Props = {
  sku: string;
  imageSrc: string;
  navigationType: GalleryTriggerType;
  priority?: boolean;
  alt?: string;
};

const galleryTypeData: Record<
  GalleryTriggerType,
  {
    testId: string;
    label: string;
    icon: ReactNode;
    dataLayerOption: DataLayerPdpPayload['option'];
  }
> = {
  gallery: {
    testId: 'PDP_GALLERY_BUTTON_GALLERY',
    label: 'Gallery',
    icon: <Icon name="gallery" />,
    dataLayerOption: 'GALLERY',
  },
  imperfections: {
    testId: 'PDP_GALLERY_BUTTON_IMPERFECTION',
    label: 'Imperfection',
    icon: <Icon name="imperfections" />,
    dataLayerOption: 'IMPERFECTIONS',
  },
  interior: {
    testId: 'PDP_GALLERY_BUTTON_INTERIOR',
    label: 'Interior',
    icon: <Icon name="interior" />,
    dataLayerOption: 'INTERIOR',
  },
};

export const CarGalleryTrigger: FC<Props> = ({ sku, imageSrc, navigationType, priority, alt }) => {
  const triggerData = galleryTypeData[navigationType];
  const { setCurrentModal } = pdpStore(
    ({ setCurrentModal }) => ({
      setCurrentModal,
    }),
    shallow,
  );

  if (!triggerData) {
    return null;
  }

  return (
    <button
      type="button"
      aria-label={`View ${triggerData.label}`}
      className="group absolute top-0 left-0 h-full w-full cursor-pointer overflow-hidden rounded-lg"
      onClick={() => {
        setCurrentModal({
          name: 'gallery',
          initialState: navigationType,
        });
        pushToDataLayerPDP(DataLayerPdpEvent.pdp_cta_interaction, {
          sku: sku,
          option: triggerData.dataLayerOption,
        });
      }}
      data-testid={triggerData.testId}
    >
      <Image
        src={imageSrc}
        className="absolute top-0 left-0 h-full w-full object-cover transition-transform duration-200 group-hover:scale-110"
        alt={alt ?? ''}
        width={400}
        height={225}
        priority={priority}
      />
      <span className="c-shade_container absolute bottom-2 left-2 flex h-10 items-center justify-center overflow-hidden rounded-sm bg-tex-inv-tie2 p-2 transition-colors duration-200 group-hover:bg-for-pri-lig group-focus:bg-tex-inv-tie2">
        <Shade noBackground />
        {triggerData.icon}
        <span className="c-fs-body2 block px-2 font-semibold">{triggerData.label}</span>
      </span>
    </button>
  );
};
